/**
 * ProseMirror Setup: Custom menu and plugin configuration
 *
 * This module provides functions for building the ProseMirror editor's toolbar,
 * keyboard shortcuts, input rules, and plugins.
 *
 * For general ProseMirror concepts, see prosemirror/README.md
 */

import { baseKeymap, toggleMark } from "prosemirror-commands";
import { dropCursor } from "prosemirror-dropcursor";
import { gapCursor } from "prosemirror-gapcursor";
import { history, redo, undo } from "prosemirror-history";
import { ellipsis, emDash, inputRules, smartQuotes } from "prosemirror-inputrules";
import { keymap } from "prosemirror-keymap";
import { type MenuElement, menuBar as toolbar } from "prosemirror-menu";
import type { Command } from "prosemirror-state";

import type { Schema } from "../adapter";
import { enterCommand, spaceCommand } from "../commands";
import { toggleBlockTypeItem } from "../toolbar/block-toggle";
import { DynamicColorPicker } from "../toolbar/color-picker";
import { imageItem } from "../toolbar/image";
import { linkItem } from "../toolbar/link";
import { markItem } from "../toolbar/mark-toggle";
import { DynamicTextStyleDropdown } from "../toolbar/text-style-dropdown";

import { t } from "i18n/i18n";
import {
  toolbarBoldIcon,
  toolbarBulletListIcon,
  toolbarItalicIcon,
  toolbarNumberedListIcon,
  toolbarUnderlineIcon,
} from "mds/utils/toolbar-icons";

/**
 * Build input rules that transform text as you type
 *
 * Input rules are like markdown shortcuts - they automatically convert text patterns
 * into formatted content. For example, typing "--" becomes an em dash.
 *
 * @param schema - The ProseMirror schema
 * @returns A plugin containing all input rules
 */
function buildInputRules() {
  // Basic text transformations: smart quotes, ellipsis, em dashes
  const rules = smartQuotes.concat(ellipsis, emDash);

  // Return a plugin that applies these rules as the user types
  return inputRules({ rules });
}

/**
 * Build keyboard shortcuts (keymap) for the editor
 *
 * Keymaps map keyboard combinations to commands. "Mod" is a platform-agnostic
 * modifier key (Cmd on Mac, Ctrl on Windows/Linux).
 *
 * @param schema - The ProseMirror schema
 * @returns A map of key combinations to commands
 */
function buildKeymap(schema: Schema): Record<string, Command> {
  const keys: Record<string, Command> = {};

  // History shortcuts: undo and redo
  keys["Mod-z"] = undo; // Cmd/Ctrl+Z
  keys["Shift-Mod-z"] = redo; // Cmd/Ctrl+Shift+Z

  // Mark shortcuts: toggle formatting with keyboard
  if (schema.marks.bold) {
    // Bold: Cmd/Ctrl+B (both lowercase and uppercase)
    keys["Mod-b"] = toggleMark(schema.marks.bold);
    keys["Mod-B"] = toggleMark(schema.marks.bold);
  }
  if (schema.marks.italic) {
    // Italic: Cmd/Ctrl+I (both lowercase and uppercase)
    keys["Mod-i"] = toggleMark(schema.marks.italic);
    keys["Mod-I"] = toggleMark(schema.marks.italic);
  }

  // custom handling for list items
  keys.Enter = enterCommand;
  keys.Space = spaceCommand;

  return keys;
}

/**
 * Menu item creation helpers
 *
 * These functions create menu items for the toolbar, each performing a schema check
 * and returning the appropriate menu element or null if the schema doesn't support it.
 */
function createTextStyleDropdownItem(schema: Schema): MenuElement | null {
  if (!schema.nodes.heading || !schema.nodes.paragraph) return null;
  return new DynamicTextStyleDropdown(schema);
}

function createBoldItem(schema: Schema): MenuElement | null {
  if (!schema.marks.bold) return null;

  return markItem(schema.marks.bold, {
    title: t("worksheet.rich_text_editor.toggle_bold"),
    icon: toolbarBoldIcon,
  });
}

function createItalicItem(schema: Schema): MenuElement | null {
  if (!schema.marks.italic) return null;

  return markItem(schema.marks.italic, {
    title: t("worksheet.rich_text_editor.toggle_italic"),
    icon: toolbarItalicIcon,
  });
}

function createUnderlineItem(schema: Schema): MenuElement | null {
  if (!schema.marks.underline) return null;

  return markItem(schema.marks.underline, {
    title: t("worksheet.rich_text_editor.toggle_underline"),
    icon: toolbarUnderlineIcon,
  });
}

function createLinkItem(schema: Schema): MenuElement | null {
  if (!schema.marks.link) return null;
  return linkItem(schema.marks.link);
}

function createImageItem(schema: Schema): MenuElement | null {
  if (!schema.nodes.image) return null;
  return imageItem(schema);
}

function createOrderedListItem(schema: Schema): MenuElement | null {
  if (!schema.nodes.ordered_list_item) return null;

  return toggleBlockTypeItem(schema.nodes.ordered_list_item, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.ordered_list_item"),
    icon: toolbarNumberedListIcon,
  });
}

function createBulletListItem(schema: Schema): MenuElement | null {
  if (!schema.nodes.bullet_list_item) return null;

  return toggleBlockTypeItem(schema.nodes.bullet_list_item, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.bullet_list_item"),
    icon: toolbarBulletListIcon,
  });
}

function createColorPickerItem(schema: Schema): MenuElement | null {
  if (!schema.marks.color) return null;
  return new DynamicColorPicker(schema);
}

/**
 * Build a menu group from multiple creator functions, filtering out null values
 */
function buildGroup(
  schema: Schema,
  ...creators: Array<(schema: Schema) => MenuElement | null>
): MenuElement[] {
  return creators
    .map((creator) => creator(schema))
    .filter((item): item is MenuElement => item !== null);
}

/**
 * Build the menu structure for the toolbar
 *
 * The menu is organized as an array of menu groups (rows). Each group is an array
 * of menu elements (buttons, dropdowns, etc.) that appear together in the toolbar.
 *
 * @param schema - The ProseMirror schema
 * @returns An array of menu groups, where each group is a row in the toolbar
 */
function buildToolbar(schema: Schema): MenuElement[][] {
  return [
    // Group 1: Text style dropdown
    buildGroup(schema, createTextStyleDropdownItem),
    // Group 2: Bold, Italic, Underline, Link, Quote buttons
    buildGroup(schema, createBoldItem, createItalicItem, createUnderlineItem, createLinkItem),
    // Group 3: Image, Code, Formula buttons
    buildGroup(schema, createImageItem),
    // Group 4: Left, Center, Right alignment buttons
    [],
    // Group 5: Numbered List, Bulleted List, Indent, Outdent, and Color Picker buttons
    buildGroup(schema, createOrderedListItem, createBulletListItem, createColorPickerItem),
  ];
}

/**
 * Create all plugins for the ProseMirror editor
 *
 * Plugins extend the editor's functionality. This function assembles all the plugins
 * needed for a fully functional rich text editor.
 *
 * @param schema - The ProseMirror schema defining document structure
 * @param showToolbar - Whether to include the toolbar
 * @returns An array of plugins to be passed to EditorState.create()
 */
export function createProseMirrorPlugins(schema: Schema, showToolbar: boolean) {
  const plugins = [
    // Input rules: Transform text as you type (smart quotes, ellipsis, em dashes, etc.)
    buildInputRules(),
    // Custom keyboard shortcuts (Cmd+B for bold, Cmd+I for italic, etc.)
    keymap(buildKeymap(schema)),
    // Base keymap: Standard editing shortcuts (Enter, Backspace, Arrow keys, etc.)
    keymap(baseKeymap),
    // Drop cursor: Shows a visual indicator when dragging content to drop it
    dropCursor(),
    // Gap cursor: Allows clicking in empty areas between block nodes
    gapCursor(),
    // History: Enables undo/redo functionality
    history(),
  ];

  // Conditionally add the menu bar (toolbar) if not in read-only mode
  if (showToolbar) {
    plugins.push(
      toolbar({
        floating: false, // Fixed at the bottom
        position: "after", // Below the editor
        content: buildToolbar(schema),
      }),
    );
  }

  return plugins;
}
